/**
 * JoinRoom.tsx
 * Mobile-first student join page. Student enters their name and room code.
 * Anonymous — no account required.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ensureAnonymousAuth } from '@db/firebase';
import { getRoom } from '@db/roomService';
import { isValidRoomCode, normalizeRoomCode } from '@utils/codeGenerator';

export default function JoinRoom(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  // Prefill the code when the student opens a join link (…/#/join?code=GURO-XXXX).
  const [roomCode, setRoomCode] = useState(() => searchParams.get('code') ?? '');
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const navigate = useNavigate();

  const handleJoin = async () => {
    setError('');
    const normalized = normalizeRoomCode(roomCode);
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!isValidRoomCode(normalized)) { setError('Invalid room code. Example: GURO-4F2A'); return; }

    setJoining(true);
    try {
      await ensureAnonymousAuth();
      const room = await getRoom(normalized);
      if (!room) { setError('Room not found. Check the code and try again.'); return; }
      if (room.status === 'closed') { setError('This class session has ended.'); return; }

      // Store student session in sessionStorage
      sessionStorage.setItem('gh_student_name', name.trim());
      sessionStorage.setItem('gh_room_code', normalized);
      sessionStorage.setItem('gh_class_id', room.classId);

      navigate('/join/play'); // single play view that follows the active activity
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6">
      <motion.div
        className="w-full max-w-sm space-y-6"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="text-6xl">🎓</div>
          <h1 className="text-2xl font-black gradient-text">GuroHub</h1>
          <p className="text-sm text-slate-500">Join your teacher's interactive class session</p>
        </div>

        {/* Form */}
        <div className="glass rounded-3xl p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Juan dela Cruz"
              maxLength={40}
              className="w-full bg-surface-elevated border border-surface-border rounded-2xl px-4 py-3 text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Room Code</label>
            <input
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="GURO-4F2A"
              maxLength={9}
              className="w-full bg-surface-elevated border border-surface-border rounded-2xl px-4 py-3 text-xl text-center font-mono font-bold text-brand-300 placeholder-slate-400 focus:outline-none focus:border-brand-500 transition-colors tracking-widest"
            />
          </div>

          {error && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-xs text-red-400 text-center bg-red-500/10 rounded-xl px-3 py-2 border border-red-500/20">
              {error}
            </motion.p>
          )}

          <motion.button
            onClick={handleJoin}
            disabled={joining}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-brand-500 to-accent-500 text-white font-bold text-base disabled:opacity-60 transition-all"
          >
            {joining ? 'Joining…' : '🚀 Join Class'}
          </motion.button>
        </div>

        <p className="text-center text-[11px] text-slate-400">
          No account needed · Powered by GuroHub
        </p>
      </motion.div>
    </div>
  );
}
